import { useSelector } from "react-redux";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";
import Table from "@kikoff/utils/src/table";

import { RootState } from "@store";

import { createLoadableSelector, thunk } from "../utils";

import { selectExperimentVariant } from "./experiment";

const initialState = {
  offerByProductName: {} as MarketplaceOffer.ByProductName,
  offerProductNamesByRequestType: {} as Record<
    web.public_.ListMarketplaceOffersRequest.RequestType,
    MarketplaceOffer.ProductName[]
  >,
  personalLoanPreviewOffers: null as MarketplaceLoanOffer[],
  personalLoanOffers: null as MarketplaceLoanOffer[],
  specialOffers: null as MarketplaceSpecialOffer[],
};

export type MarketplaceState = typeof initialState;

const marketplaceSlice = createSlice({
  name: "marketplace",
  initialState,
  reducers: {
    setMarketplaceOffers(
      state,
      {
        payload,
      }: PayloadAction<{
        marketplaceOffers: MarketplaceOffer[];
        requestType: web.public_.ListMarketplaceOffersRequest.RequestType;
      }>
    ) {
      state.offerProductNamesByRequestType[
        payload.requestType
      ] = payload.marketplaceOffers.map(({ productName }) => productName);

      Object.assign(
        state.offerByProductName,
        MarketplaceOffer.ByProductName.fromList(payload.marketplaceOffers)
      );
    },
    removeMarketplaceOffer(
      state,
      { payload }: PayloadAction<MarketplaceOffer.ProductName>
    ) {
      for (const [requestType, offerProductNames] of Object.entries(
        state.offerProductNamesByRequestType
      )) {
        const offerIndex = offerProductNames.indexOf(payload);
        if (offerIndex === -1) continue;
        state.offerProductNamesByRequestType[requestType].splice(offerIndex, 1);
      }
    },
    setPersonalLoanPreviewOffers(
      state,
      { payload }: PayloadAction<MarketplaceLoanOffer[]>
    ) {
      state.personalLoanPreviewOffers = payload;
    },
    setPersonalLoanOffers(
      state,
      { payload }: PayloadAction<MarketplaceLoanOffer[]>
    ) {
      state.personalLoanOffers = payload;
    },
    setSpecialOffers(
      state,
      { payload }: PayloadAction<MarketplaceSpecialOffer[]>
    ) {
      state.specialOffers = payload;
    },
  },
});

const { actions } = marketplaceSlice;
export const { setPersonalLoanOffers, setPersonalLoanPreviewOffers } = actions;
export default marketplaceSlice.reducer;

export const selectMarketplaceOffers = createLoadableSelector(
  (
    requestType: web.public_.ListMarketplaceOffersRequest.RequestType,
    offerType?: web.public_.MarketplaceOffer.OfferType
  ) => (state: RootState) =>
    state.marketplace?.offerProductNamesByRequestType[requestType]
      ?.map((productName) => state.marketplace.offerByProductName[productName])
      .filter((offer) => !offerType || offer.type === offerType),
  {
    loadAction: (requestType) => fetchMarketplaceOffers(requestType),
    selectLoaded: (requestType) => (state: RootState) =>
      !!state.marketplace?.offerProductNamesByRequestType[requestType],
  }
);

export const selectLoanPreviewOffers = createLoadableSelector(
  () => (state: RootState) => state.marketplace?.personalLoanPreviewOffers,
  { loadAction: () => fetchLoanPreviewOffers() }
);

export const selectLoanOffers = () => (state: RootState) =>
  state.marketplace?.personalLoanOffers;
export const selectSpecialOffers = () => (state: RootState) =>
  state.marketplace?.specialOffers;

export const fetchMarketplaceOffers = Object.assign(
  (requestType) =>
    thunk((dispatch) => {
      return webRPC.Marketplace.listMarketplaceOffers({
        requestType,
      }).then(
        handleProtoStatus({
          SUCCESS({ offers }) {
            dispatch(
              actions.setMarketplaceOffers({
                marketplaceOffers: offers,
                requestType,
              })
            );
            return offers;
          },
          _DEFAULT: handleFailedStatus("Failed to load live utilization."),
        })
      );
    }),
  {
    ifNotPresent: (requestType) =>
      thunk((dispatch, getState) =>
        Promise.resolve(
          selectMarketplaceOffers(requestType)(getState()) ||
            dispatch(fetchMarketplaceOffers(requestType))
        )
      ),
  }
);

export const dismissMarketplaceOffer = (
  productName: MarketplaceOffer.ProductName
) =>
  thunk((dispatch, getState) =>
    webRPC.Marketplace.dismissMarketplaceOffer({ productName }).then(
      handleProtoStatus({
        SUCCESS() {
          dispatch(actions.removeMarketplaceOffer(productName));
        },
        _DEFAULT: handleFailedStatus("Failed to dismiss Marketplace offer."),
      })
    )
  );

export const submitLoanPreview = (
  loanPreview: web.public_.ISubmitLoanPreviewRequest
) =>
  thunk(() =>
    webRPC.Marketplace.submitLoanPreview(loanPreview).then(
      handleProtoStatus({
        SUCCESS() {},
        _DEFAULT: handleFailedStatus("Failed to submit loan preview."),
      })
    )
  );

export const submitLoan = (loanForm: web.public_.ISubmitLoanRequest) =>
  thunk(() =>
    webRPC.Marketplace.submitLoan(loanForm).then(
      handleProtoStatus({
        SUCCESS() {},
        _DEFAULT: handleFailedStatus("Failed to submit loan."),
      })
    )
  );
export const fetchLoanPreviewOffers = () =>
  thunk((dispatch) =>
    webRPC.Marketplace.listLoanPreviewOffers({}).then(
      handleProtoStatus({
        SUCCESS({ offers }) {
          dispatch(actions.setPersonalLoanPreviewOffers(offers));
          return offers;
        },
        PENDING: () => false,
        _DEFAULT: handleFailedStatus("Failed to fetch loan preview offers."),
      })
    )
  );
export const fetchLoanOffers = () =>
  thunk((dispatch) =>
    webRPC.Marketplace.listLoanOffers({}).then(
      handleProtoStatus({
        SUCCESS({ offers, specialOffers }) {
          dispatch(actions.setPersonalLoanOffers(offers));
          dispatch(actions.setSpecialOffers(specialOffers));
          return offers;
        },
        PENDING: () => false,
        _DEFAULT: handleFailedStatus("Failed to fetch loan offers."),
      })
    )
  );
export const sendCreditCardOfferContinuedEvent = (
  offerContinued: web.public_.ISendCreditCardOfferContinuedEventRequest
) =>
  thunk(() =>
    webRPC.Marketplace.sendCreditCardOfferContinuedEvent(offerContinued).then(
      handleProtoStatus({
        SUCCESS() {},
        _DEFAULT: handleFailedStatus(
          "Failed to send credit card offer continued event."
        ),
      })
    )
  );
export const sendLoanConfirmedOfferTappedEvent = (
  loanTapped: web.public_.ISendLoanConfirmedOfferTappedEventRequest
) =>
  webRPC.Marketplace.sendLoanConfirmedOfferTappedEvent(loanTapped).then(
    handleProtoStatus({
      SUCCESS() {},
      _DEFAULT: handleFailedStatus(
        "Failed to send loan confirmed offer tapped event."
      ),
    })
  );

export namespace Marketplace {}

export const useMarketplaceEnabled = () => {
  const [marketplaceHoldout, loadingMarketplaceHoldout] = useSelector(
    selectExperimentVariant.load("marketplaceHoldout")
  );

  return !loadingMarketplaceHoldout && marketplaceHoldout === "control";
};

export type MarketplaceOffer = web.public_.IMarketplaceOffer;
export namespace MarketplaceOffer {
  export type ProductName = string & {};

  export type ByProductName = Record<
    MarketplaceOffer.ProductName,
    MarketplaceOffer
  >;

  export type OfferType = typeof web.public_.MarketplaceOffer.OfferType[keyof typeof web.public_.MarketplaceOffer.OfferType];
  export const OfferType = web.public_.MarketplaceOffer.OfferType;

  export namespace ByProductName {
    export const fromList = (marketplaceOffers: MarketplaceOffer[]) =>
      Table.createIndex(marketplaceOffers, ["productName"]);
  }

  export type RequestType = typeof web.public_.ListMarketplaceOffersRequest.RequestType[keyof typeof web.public_.ListMarketplaceOffersRequest.RequestType];
  export const RequestType =
    web.public_.ListMarketplaceOffersRequest.RequestType;
}

export type MarketplaceLoanOffer = web.public_.IMarketplaceLoanOffer;
export type MarketplaceSpecialOffer = web.public_.IMarketplaceSpecialOffer;
export namespace MarketplaceLoanOffer {
  export type LoanPurpose = typeof web.public_.MarketplacePersonalLoan.Purpose[keyof typeof web.public_.MarketplacePersonalLoan.Purpose];
  export const loanPurposes = Object.keys(
    web.public_.MarketplacePersonalLoan.Purpose
  );

  export const LoanPurpose = web.public_.MarketplacePersonalLoan.Purpose;
  export const DisplayNameByPurpose = {
    UNKNOWN: "Unknown",
    AUTO: "Auto",
    AUTO_PURCHASE: "Auto Purchase",
    AUTO_REFINANCE: "Auto Refinance",
    BABY: "Baby",
    BOAT: "Boat",
    BUSINESS: "Business",
    CAR_REPAIR: "Car Repair",
    COSMETIC: "Cosmetic",
    CREDIT_CARD_REFI: "Credit Card Refi",
    DEBT_CONSOLIDATION: "Debt Consolidation",
    EMERGENCY: "Emergency",
    ENGAGEMENT: "Engagement",
    GREEN: "Green",
    HOME_IMPROVEMENT: "Home Improvement",
    HOME_PURCHASE: "Home Purchase",
    HOME_REFI: "Home Refi",
    HOUSEHOLD_EXPENSES: "Household Expenses",
    LARGE_PURCHASES: "Large Purchases",
    LIFE_EVENT: "Life Event",
    MEDICAL_DENTAL: "Medical Dental",
    MOTORCYCLE: "Motorcycle",
    MOVING_RELOCATION: "Moving Relocation",
    OTHER: "Other",
    RV: "RV",
    SPECIAL_OCCASION: "Special Occasion",
    STUDENT_LOAN: "Student Loan",
    STUDENT_LOAN_REFI: "Student Loan Refi",
    TAXES: "Taxes",
    VACATION: "Vacation",
    WEDDING: "Wedding",
  };

  export type EmploymentStatus = web.public_.SubmitLoanRequest.EmploymentStatus;
  export const employmentStatuses = [
    "EMPLOYED_FULL_TIME",
    "EMPLOYED_PART_TIME",
    "MILITARY",
    "NOT_EMPLOYED",
    "SELF_EMPLOYED",
    "RETIRED",
    "OTHER",
  ];
  export const displayNameByEmploymentStatus = {
    EMPLOYED_FULL_TIME: "I work 30+ hours per week.",
    EMPLOYED_PART_TIME: "I work less than 30 hours per week.",
    MILITARY: "I’m in the military.",
    NOT_EMPLOYED: "I’m currently unemployed.",
    SELF_EMPLOYED: "I work for myself.",
    RETIRED: "I’m retired.",
    OTHER: "My employment state is different.",
  };
  export type EmploymentPayFrequency = web.public_.SubmitLoanRequest.EmploymentPayFrequency;
}
